import type { ReactElement } from 'react';
import { useMemo, useState } from 'react';
import type { AdminMenuItemType } from '@cookspace-shared-contracts/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
    closestCenter,
    DndContext,
    PointerSensor,
    useSensor,
    useSensors
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

import { MenuItemTypeNode } from '@/components/MenuItemTypeNode';
import type { MenuItemTypeTreeNode } from '@/lib/buildMenuItemTypeTree';
import {
    buildMenuItemTypeTree,
    collectMenuItemTypeDescendantIds
} from '@/lib/buildMenuItemTypeTree';

export interface MenuItemTypeReorderPayload {
    parentId: string | null;
    orderedIds: string[];
}

function findNodeInTree(
    nodes: MenuItemTypeTreeNode[],
    id: string
): MenuItemTypeTreeNode | undefined {
    for (const n of nodes) {
        if (n.id === id) {
            return n;
        }
        const inner = findNodeInTree(n.children, id);
        if (inner) {
            return inner;
        }
    }
    return undefined;
}

interface MenuItemTypeTreeProps {
    items: AdminMenuItemType[];
    onEdit: (node: MenuItemTypeTreeNode) => void;
    onDelete: (node: MenuItemTypeTreeNode) => void;
    onAddChild: (node: MenuItemTypeTreeNode) => void;
    onReorder?: (payload: MenuItemTypeReorderPayload) => void;
    onMove?: (nodeId: string, newParentId: string | null) => void;
    draggable?: boolean;
}

/**
 * Дерево системных типов меню (зеркало SpecializationTree).
 * Drag-and-drop: drop на узел — перемещение в дети (move), drop на соседа
 * внутри одного родителя — переупорядочивание (reorder).
 *
 * Раскрытие/сворачивание узлов — локальный state, чтобы при обновлении
 * списка через RTK не сбрасывалось состояние.
 */
export function MenuItemTypeTree({
    items,
    onEdit,
    onDelete,
    onAddChild,
    onReorder,
    onMove,
    draggable = true
}: MenuItemTypeTreeProps): ReactElement {
    const [expanded, setExpanded] = useState<Set<string>>(() => {
        // По умолчанию раскрываем корневые узлы.
        return new Set(items.filter((i) => i.parentId === null).map((i) => i.id));
    });

    const tree = useMemo(() => buildMenuItemTypeTree(items), [items]);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
    );

    const toggleExpanded = (id: string): void => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const handleDragEnd = (event: DragEndEvent): void => {
        const { active, over } = event;
        if (!over || active.id === over.id) {
            return;
        }

        const activeId = String(active.id);
        const overData = over.data.current as { type?: string; nodeId?: string } | undefined;

        const activeNode = items.find((i) => i.id === activeId);
        if (!activeNode) {
            return;
        }

        // Случай 1: drop на сам узел (через droppable) — перемещение в дети.
        if (overData?.type === 'drop-onto' && overData.nodeId !== undefined) {
            const targetNodeId = overData.nodeId;
            if (targetNodeId === activeId) {
                return;
            }

            // Запрет цикла — проверим, не является ли target потомком active
            // (сервер всё равно проверит — 400 MENU_ITEM_TYPE_CYCLE_DETECTED).
            const activeTreeNode = findNodeInTree(tree, activeId);
            if (activeTreeNode) {
                const descendantIds = collectMenuItemTypeDescendantIds(activeTreeNode);
                if (descendantIds.has(targetNodeId)) {
                    return;
                }
            }

            if (activeNode.parentId !== targetNodeId && onMove) {
                onMove(activeId, targetNodeId);
            }
            return;
        }

        // Случай 2: drop на соседний узел (sortable) — переупорядочивание
        // внутри группы (same parent).
        const overId = String(over.id);
        const overNode = items.find((i) => i.id === overId);
        if (!overNode) {
            return;
        }

        if (activeNode.parentId === overNode.parentId && onReorder) {
            // Собираем все узлы этой группы в исходном порядке tree.
            const parentId = activeNode.parentId;
            const sibling = parentId === null
                ? tree
                : findNodeInTree(tree, parentId)?.children ?? [];
            const orderedIds = sibling.map((n) => n.id);
            const fromIdx = orderedIds.indexOf(activeId);
            const toIdx = orderedIds.indexOf(overId);
            if (fromIdx === -1 || toIdx === -1) {
                return;
            }
            orderedIds.splice(fromIdx, 1);
            orderedIds.splice(toIdx, 0, activeId);
            onReorder({ parentId, orderedIds });
        }
    };

    const renderNode = (node: MenuItemTypeTreeNode, level: number): ReactElement => {
        const isExpanded = expanded.has(node.id);
        return (
            <MenuItemTypeNode
                key={node.id}
                node={node}
                level={level}
                expanded={isExpanded}
                onToggleExpanded={toggleExpanded}
                onEdit={onEdit}
                onDelete={onDelete}
                onAddChild={onAddChild}
                draggable={draggable}
            >
                {node.children.length > 0 ? (
                    <SortableContext
                        items={node.children.map((c) => c.id)}
                        strategy={verticalListSortingStrategy}
                    >
                        <>{node.children.map((child) => renderNode(child, level + 1))}</>
                    </SortableContext>
                ) : (
                    <></>
                )}
            </MenuItemTypeNode>
        );
    };

    if (!draggable) {
        return (
            <div data-testid="menu-item-type-tree">
                {tree.map((root) => renderNode(root, 0))}
            </div>
        );
    }

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
        >
            <SortableContext
                items={tree.map((n) => n.id)}
                strategy={verticalListSortingStrategy}
            >
                <div data-testid="menu-item-type-tree">
                    {tree.map((root) => renderNode(root, 0))}
                </div>
            </SortableContext>
        </DndContext>
    );
}
